import { useEffect, useMemo, useRef, useState } from 'react';
import type { MinigameViewProps } from '../../types/minigame';

// Counter-Intrusion Duel (level 85) — asynchronous PvP in two phases.
//
// PHASE 1 defence: the opponent's waves land one at a time; deploy the matching countermeasure
// inside the window. Answering closes that window immediately and the next wave lands after a
// short beat, so there is no dead time once you've dealt with a wave.
//
// Your RUNNING defences decide how well you can even READ an incoming wave: on a branch where
// their attack out-classes your counter-tool the signature is partial (two candidates) or gone
// entirely (all four), and one wave in two is partial anyway. That is where gear weighs on
// the outcome — the clock never can, since anyone picks 1 of 4 known buttons inside any window
// that is fair on mobile.
// PHASE 2 attack: allocate your own waves across the four vectors, knowing both sides' gear —
// each repeat of a vector makes them adapt.
//
// The server owns phase 1 entirely: every answer is a round-trip that it times against its own
// clock and grades, and the sequence is revealed one wave at a time. What this component holds
// is the state the server last told us, never a verdict of its own.

const WAVES = ['BRUTE', 'EXPLOIT', 'DDOS', 'TROJAN'] as const;
type WaveKind = typeof WAVES[number];

/** Each vector is repelled by exactly one defensive tool (mirrors IntrusionWave.Counter). */
const COUNTER: Record<WaveKind, string> = {
  BRUTE: 'PasswordHashing',
  EXPLOIT: 'Firewall',
  DDOS: 'DdosBreaker',
  TROJAN: 'AntiVirus',
};

const WAVE_COLOR: Record<WaveKind, string> = {
  BRUTE: '#f87171',
  EXPLOIT: '#fbbf24',
  DDOS: '#60a5fa',
  TROJAN: '#c084fc',
};

// The grading constants are NOT duplicated here: they arrive with the challenge. A local copy
// drifted from the server once already — the projection promised six landing waves while the
// grader, using a proportional rule, landed two.
const FALLBACK_MARGIN = 0.05;
const FALLBACK_ADAPT = 0.12;

/** Grace before we ask the server to settle an expired window, covering clock skew. */
const CATCHUP_GRACE_MS = 500;

interface Resolved { index: number; kind: WaveKind; counter: string | null; repelled: boolean }

type Band = 'shielded' | 'even' | 'exposed' | 'blind';

/** The live state of the duel, as last reported by the server. */
interface Live {
  currentIndex: number;
  /** What the wave could be: 1 entry when read cleanly, 2 on a partial signature, 4 when blind. */
  currentCandidates: WaveKind[];
  currentBand: Band | null;
  /** This wave's own window, already squeezed by its band. */
  currentWindowMs: number;
  currentOpenedMs: number;
  /** Only when the pre-announce perk is earned — and only as clearly as the sensors read it. */
  nextCandidates: WaveKind[];
  defenceOver: boolean;
  resolved: Resolved[];
}

interface Challenge extends Live {
  waveCount: number;
  reactionMs: number;
  lullMs: number;
  preannounce: boolean;
  opponentName: string;
  opponentLevel: number;
  opponentOrigin: string;
  opponentSynthetic: boolean;
  opponentDefence: Record<string, number>;
  /** Their attack versions by vector — what your running defences are measured against. */
  opponentOffence: Record<string, number>;
  myOffence: Record<string, number>;
  myDefence: Record<string, number>;
  /** Per-vector band, so you can see which branches you are weak on before a wave lands. */
  bands: Record<string, Band>;
  /** How much attacking beats an equal defence by, and what each repeat costs. From the server. */
  attackerMargin: number;
  adaptStep: number;
  /** The branch whose first wave always lands, whatever the numbers say. */
  weakestVector: string | null;
}

const BAND_COLOR: Record<Band, string> = {
  shielded: '#4ade80',
  even: '#94a3b8',
  exposed: '#fbbf24',
  blind: '#f87171',
};

const BAND_NOTE: Record<Band, string> = {
  shielded: 'your tool out-classes theirs — read cleanly, and pre-announced',
  even: 'evenly matched — read when it lands',
  exposed: 'they out-class you — only a partial signature',
  blind: 'badly out-classed — the wave arrives unidentified',
};

const ORIGIN_LABEL: Record<string, string> = {
  peer: 'recorded opponent, matched by level',
  calibrated: 'recorded opponent, calibrated to your level',
  self: 'your own previous run',
  simulated: 'simulated adversary',
};

/**
 * Stat look-up that doesn't care about key casing. These dictionaries travel through Cosmos,
 * which camelCases dictionary keys, so a ghost's defence can arrive as "passwordHashing" while
 * we ask for "PasswordHashing" — reading 0 and showing v0.0 for a fully-armed opponent. The
 * server normalises them now; this keeps the display right for anything already in flight.
 */
function stat(map: Record<string, number> | undefined, key: string): number {
  if (!map) return 0;
  if (map[key] !== undefined) return map[key];
  const found = Object.keys(map).find(k => k.toLowerCase() === key.toLowerCase());
  return found === undefined ? 0 : map[found];
}

const liveOf = (c: Challenge | null): Live => ({
  currentIndex: c?.currentIndex ?? 0,
  currentCandidates: c?.currentCandidates ?? [],
  currentBand: c?.currentBand ?? null,
  currentWindowMs: c?.currentWindowMs ?? 0,
  currentOpenedMs: c?.currentOpenedMs ?? 0,
  nextCandidates: c?.nextCandidates ?? [],
  defenceOver: c?.defenceOver ?? false,
  resolved: c?.resolved ?? [],
});

export default function CounterIntrusionView({
  challenge, onSubmit, onAct, setPendingSubmission, startedAgoMs, busy,
}: MinigameViewProps<Challenge, Live>) {
  const waveCount = challenge?.waveCount ?? 5;
  const reactionMs = challenge?.reactionMs ?? 3500;
  const preannounce = challenge?.preannounce ?? false;

  // Clock anchored to THIS challenge during render rather than inside an effect — see the
  // note in PortScanSweepView: an effect that re-fires would freeze the wave schedule.
  // The anchor is pulled BACK by however long the attempt has already been running, so a
  // resumed attempt lines up with the server's clock instead of restarting the timeline.
  const anchorRef = useRef<{ key: unknown; at: number }>({ key: challenge, at: Date.now() - startedAgoMs });
  if (anchorRef.current.key !== challenge) anchorRef.current = { key: challenge, at: Date.now() - startedAgoMs };

  const [live, setLive] = useState<Live>(() => liveOf(challenge));
  const [elapsed, setElapsed] = useState(0);
  const [attack, setAttack] = useState<WaveKind[]>([]);
  // Answers in flight must not double-fire, and a settled window must be reported once.
  const sentRef = useRef<number | null>(null);
  const catchupRef = useRef<number | null>(null);

  useEffect(() => {
    setLive(liveOf(challenge));
    setAttack([]);
    sentRef.current = null;
    catchupRef.current = null;
    setPendingSubmission({ attack: [] });
    // setPendingSubmission intentionally omitted — see PortScanSweepView.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challenge]);

  useEffect(() => {
    const id = setInterval(() => setElapsed(Date.now() - anchorRef.current.at), 50);
    return () => clearInterval(id);
  }, []);

  // Per wave, not per duel: a branch you are out-classed on gets a squeezed window.
  const waveWindowMs = live.currentWindowMs > 0 ? live.currentWindowMs : reactionMs;
  const windowEndsMs = live.currentOpenedMs + waveWindowMs;
  const landed = !live.defenceOver && elapsed >= live.currentOpenedMs;
  const expired = !live.defenceOver && elapsed > windowEndsMs + CATCHUP_GRACE_MS;
  const answering = sentRef.current === live.currentIndex;
  const band = live.currentBand;

  // A window that ran out has to be settled by the SERVER (it costs a breach), so ask it to
  // catch up — once per wave. One call settles every window that expired, so this can't loop.
  useEffect(() => {
    if (!expired || busy || live.defenceOver) return;
    if (catchupRef.current === live.currentIndex) return;
    catchupRef.current = live.currentIndex;
    void (async () => {
      const next = await onAct({ wave: live.currentIndex, counter: null });
      if (next) setLive(next);
    })();
  }, [expired, busy, live.currentIndex, live.defenceOver, onAct]);

  const deploy = async (counter: string) => {
    // Guard on the ref, not on state: two clicks in one tick would both pass a state check.
    if (busy || live.defenceOver || !landed || expired) return;
    if (sentRef.current === live.currentIndex) return;
    sentRef.current = live.currentIndex;
    const next = await onAct({ wave: live.currentIndex, counter });
    // Released on failure, so a rejected round-trip doesn't lock the wave out for good.
    if (next) setLive(next); else sentRef.current = null;
  };

  const addWave = (kind: WaveKind) => {
    if (busy || attack.length >= waveCount) return;
    const next = [...attack, kind];
    setAttack(next);
    setPendingSubmission({ attack: next });
  };

  const popWave = () => {
    if (busy || attack.length === 0) return;
    const next = attack.slice(0, -1);
    setAttack(next);
    setPendingSubmission({ attack: next });
  };

  const margin = challenge?.attackerMargin ?? FALLBACK_MARGIN;
  const adapt = challenge?.adaptStep ?? FALLBACK_ADAPT;
  const weakest = challenge?.weakestVector ?? null;

  /** What their defence on this branch is worth against the k-th wave of the same vector. */
  const effectiveDefence = (kind: WaveKind, repeatIndex: number) =>
    stat(challenge?.opponentDefence, COUNTER[kind]) * (1 + adapt * repeatIndex);

  /**
   * Will wave k of this vector breach? Same rule the server grades with, including the weak
   * point — proportional on both sides, so it reads the same at v15 and at v1000.
   */
  const willBreach = (kind: WaveKind, repeatIndex: number) => {
    if (kind === weakest && repeatIndex === 0) return true;      // the guaranteed weak point
    return stat(challenge?.myOffence, kind) * (1 + margin) >= effectiveDefence(kind, repeatIndex);
  };

  const projected = useMemo(() => {
    const used: Record<string, number> = {};
    let hits = 0;
    for (const k of attack) {
      const r = used[k] ?? 0;
      used[k] = r + 1;
      if (willBreach(k, r)) hits++;
    }
    return hits;
    // willBreach reads only `challenge`, which is stable for the attempt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attack, challenge]);

  const repelled = live.resolved.filter(r => r.repelled).length;
  const suffered = waveCount - repelled;
  const lastResolved = live.resolved.length ? live.resolved[live.resolved.length - 1] : null;

  return (
    <div style={{ fontFamily: 'monospace' }}>
      {/* Opponent card */}
      <div style={{
        border: '1px solid var(--border-color)', borderRadius: 4, padding: '0.5rem 0.7rem',
        marginBottom: '0.7rem', background: '#0d1117',
      }}>
        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'baseline' }}>
          <strong style={{ color: '#e2e8f0', fontSize: '0.9rem' }}>{challenge?.opponentName ?? 'Unknown'}</strong>
          <span style={{ color: 'var(--text-muted)', fontSize: '0.74rem' }}>Lv {challenge?.opponentLevel ?? '—'}</span>
          <span style={{ color: challenge?.opponentSynthetic ? '#fbbf24' : '#4ade80', fontSize: '0.7rem' }}>
            {ORIGIN_LABEL[challenge?.opponentOrigin ?? 'simulated'] ?? challenge?.opponentOrigin}
          </span>
        </div>
        <div style={{ color: '#6b7280', fontSize: '0.68rem', marginTop: '0.2rem' }}>
          Their defences: {WAVES.map(w => `${COUNTER[w]} v${stat(challenge?.opponentDefence, COUNTER[w]).toFixed(1)}`).join(' · ')}
        </div>
      </div>

      {!live.defenceOver ? (
        <>
          <p style={{ color: '#cbd5e1', fontSize: '0.82rem', margin: '0 0 0.6rem', lineHeight: 1.5 }}>
            <strong>Phase 1 — hold the line.</strong> Each wave is repelled by exactly one tool.
            Your <strong>running</strong> defences decide how clearly you see it coming: where
            they out-class you, the signature is only partial.
            {preannounce && <> Your gear <span style={{ color: '#fde047' }}>pre-announces</span> what's next.</>}
          </p>

          {/* Incoming wave */}
          <div style={{
            border: `1px solid ${landed && band ? BAND_COLOR[band] : '#232833'}`,
            borderRadius: 4, padding: '0.9rem', textAlign: 'center', marginBottom: '0.6rem',
            background: landed ? '#12161d' : '#0d1117',
          }}>
            {landed && live.currentCandidates.length > 0 ? (
              <>
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                  {live.currentCandidates.map(k => (
                    <span key={k} style={{
                      color: WAVE_COLOR[k],
                      fontSize: live.currentCandidates.length === 1 ? '1.5rem' : '1.05rem',
                      fontWeight: 'bold', letterSpacing: '0.08em',
                    }}>
                      {k}
                    </span>
                  ))}
                </div>
                {live.currentCandidates.length > 1 && (
                  <div style={{ color: band ? BAND_COLOR[band] : '#94a3b8', fontSize: '0.68rem', marginTop: '0.2rem' }}>
                    {live.currentCandidates.length === 2 ? 'partial signature — one of these two' : 'unidentified — could be any of them'}
                  </div>
                )}
                <div style={{ height: 4, background: '#1a1a2e', borderRadius: 2, overflow: 'hidden', marginTop: '0.5rem' }}>
                  <div style={{
                    height: '100%',
                    width: `${Math.max(0, 100 - ((elapsed - live.currentOpenedMs) / Math.max(1, waveWindowMs)) * 100)}%`,
                    background: band ? BAND_COLOR[band] : '#94a3b8', transition: 'width 0.05s linear',
                  }} />
                </div>
                <div style={{ color: answering ? '#4ade80' : '#6b7280', fontSize: '0.72rem', marginTop: '0.35rem' }}>
                  {answering ? 'deploying…' : `wave ${live.currentIndex + 1} of ${waveCount}`}
                </div>
                {/* While this wave is live, the perk names the one AFTER it. */}
                {preannounce && live.nextCandidates.length > 0 && (
                  <div style={{ color: '#6b7280', fontSize: '0.68rem', marginTop: '0.2rem' }}>
                    then: {live.nextCandidates.map((k, i) => (
                      <span key={k} style={{ color: WAVE_COLOR[k] }}>{i > 0 ? ' / ' : ''}{k}</span>
                    ))}
                  </div>
                )}
              </>
            ) : (
              // The beat between waves: how the last one went, and what is landing next.
              <div style={{ fontSize: '0.9rem' }}>
                {lastResolved && (
                  <div style={{ color: lastResolved.repelled ? '#4ade80' : '#f87171', fontSize: '0.85rem', marginBottom: '0.3rem' }}>
                    {lastResolved.kind} {lastResolved.repelled ? 'repelled' : lastResolved.counter ? 'wrong tool — breached' : 'no answer — breached'}
                  </div>
                )}
                <div style={{ color: '#4b5563' }}>
                  {/* currentCandidates IS the wave about to land — nextCandidates is the one
                      after it, and announcing that here named a wave two ahead. */}
                  {preannounce && live.currentCandidates.length > 0
                    ? <>incoming: {live.currentCandidates.map((k, i) => (
                        <span key={k} style={{ color: WAVE_COLOR[k] }}>{i > 0 ? ' / ' : ''}{k}</span>
                      ))}</>
                    : 'scanning…'}
                </div>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
            {/* Deliberately NOT highlighted by which wave landed: knowing that PasswordHashing
                answers BRUTE is the skill this duel is about, and ringing the right button
                would let anyone play it without ever learning the pairing. The panel names the
                candidate vectors; the table below is the reference. */}
            {WAVES.map(w => (
              <button
                key={w}
                onClick={() => deploy(COUNTER[w])}
                disabled={busy || !landed || expired || answering}
                style={{ fontSize: '0.76rem', padding: '0.4rem 0.7rem' }}
                title={`Repels ${w}`}
              >
                {COUNTER[w]}
              </button>
            ))}
          </div>

          {/* Where this player is strong and weak — and the vector-to-tool reference. */}
          <div style={{ display: 'grid', gap: '0.15rem', marginBottom: '0.6rem', fontSize: '0.7rem' }}>
            {WAVES.map(w => {
              const b = (challenge?.bands?.[w] ?? 'even') as Band;
              return (
                <div key={w} style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'baseline' }}>
                  <span style={{ color: WAVE_COLOR[w], minWidth: '4.2rem' }}>{w}</span>
                  <span style={{ color: 'var(--text-muted)' }}>
                    their v{stat(challenge?.opponentOffence, w).toFixed(1)} vs your {COUNTER[w]} v{stat(challenge?.myDefence, COUNTER[w]).toFixed(1)}
                  </span>
                  <span style={{ color: BAND_COLOR[b] }} title={BAND_NOTE[b]}>{b}</span>
                </div>
              );
            })}
          </div>

          <span style={{ color: 'var(--text-muted)', fontSize: '0.76rem' }}>
            Repelled <strong style={{ color: '#4ade80' }}>{repelled}</strong> / {waveCount}
            {live.resolved.length > 0 && (
              <> · {live.resolved.map(r => (
                <span key={r.index} style={{ color: r.repelled ? '#4ade80' : '#f87171' }}>{r.repelled ? '✓' : '✗'}</span>
              ))}</>
            )}
          </span>
        </>
      ) : (
        <>
          <p style={{ color: '#cbd5e1', fontSize: '0.82rem', margin: '0 0 0.6rem', lineHeight: 1.5 }}>
            <strong>Phase 2 — strike back.</strong> Allocate <strong>{waveCount}</strong> waves.
            A wave lands when your tool reaches their defence (with a{' '}
            <strong>+{Math.round(margin * 100)}%</strong> attacker's edge) — but each repeat of the
            same vector raises that defence by <strong>+{Math.round(adapt * 100)}%</strong>, because
            they adapt. Stacking one vector stops working fast; spread them.
            {weakest && <> Their weakest branch is <strong style={{ color: WAVE_COLOR[weakest as WaveKind] }}>{weakest}</strong>, whose first wave always gets through.</>}
          </p>

          <div style={{ display: 'grid', gap: '0.3rem', marginBottom: '0.7rem' }}>
            {WAVES.map(w => {
              const mine = stat(challenge?.myOffence, w);
              const theirs = stat(challenge?.opponentDefence, COUNTER[w]);
              const usedCount = attack.filter(a => a === w).length;
              return (
                <div key={w} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', fontSize: '0.76rem' }}>
                  <button
                    onClick={() => addWave(w)}
                    disabled={busy || attack.length >= waveCount}
                    style={{ minWidth: '5.5rem', fontSize: '0.74rem', padding: '0.25rem 0.5rem', color: WAVE_COLOR[w], borderColor: WAVE_COLOR[w] }}
                  >
                    + {w}
                  </button>
                  <span style={{ color: 'var(--text-muted)' }}>
                    yours v{mine.toFixed(1)} (+{Math.round(margin * 100)}% = v{(mine * (1 + margin)).toFixed(1)}) vs theirs v{theirs.toFixed(1)}
                    {usedCount > 0 && <> → next needs v{effectiveDefence(w, usedCount).toFixed(1)}</>}
                  </span>
                  {/* Whether the NEXT wave of this vector would land, so stacking is visibly
                      hitting a wall instead of silently wasting waves. */}
                  <span style={{ color: willBreach(w, usedCount) ? '#4ade80' : '#f87171' }}>
                    {willBreach(w, usedCount) ? 'next lands' : 'next bounces'}
                  </span>
                  {usedCount > 0 && <span style={{ color: WAVE_COLOR[w] }}>×{usedCount}</span>}
                </div>
              );
            })}
          </div>

          <div style={{ marginBottom: '0.7rem', fontSize: '0.78rem' }}>
            <span style={{ color: 'var(--text-muted)' }}>Sequence: </span>
            {attack.length === 0
              ? <span style={{ color: '#4b5563' }}>empty</span>
              : attack.map((k, i) => <span key={i} style={{ color: WAVE_COLOR[k], marginRight: '0.35rem' }}>{k}</span>)}
            {attack.length > 0 && (
              <button onClick={popWave} disabled={busy} style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem', marginLeft: '0.4rem' }}>
                undo
              </button>
            )}
          </div>

          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.7rem', fontSize: '0.8rem' }}>
            <span>Breaches taken: <strong style={{ color: suffered > 0 ? '#f87171' : '#4ade80' }}>{suffered}</strong></span>
            <span>Projected inflicted: <strong style={{ color: projected > suffered ? '#4ade80' : '#fbbf24' }}>{projected}</strong></span>
            <span style={{ color: 'var(--text-muted)', fontSize: '0.74rem' }}>
              (you win when inflicted &gt; taken — a tie goes to the defender)
            </span>
          </div>

          <button
            className="primary"
            onClick={() => onSubmit({ attack })}
            disabled={busy || attack.length === 0}
            style={{ fontSize: '0.82rem', padding: '0.35rem 0.9rem' }}
          >
            {busy ? '…' : 'Launch counter-attack'}
          </button>
        </>
      )}
    </div>
  );
}
